import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { generateGUID } from "@faro-lotv/foundation";
import { AnnotationStatus, IElementType, IElementTypeHint } from "@faro-lotv/ielement-types";
import { removeIElement } from "@faro-lotv/project-source";
import { ApiResponseError, createMutationAddMarkupField, createMutationDeleteElement, createMutationUserDirectoryMarkupField } from "@faro-lotv/service-wires";
import { createAttachmentMutation } from "@pages/project-details/project-markups/markup-attachment-utils";
import { createEditStatusMutation } from "@pages/project-details/project-markups/status/markup-status-utils";
import { removeIElements } from "@store/i-elements/i-elements-slice-thunk";
import { shouldDeleteModel3D } from "@store/markups/markups-selector-utils";
import { useAppDispatch } from "@store/store-helper";
import { AnnotationEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

/** Custom hook that takes care of fetching all markup elements and other associated elements */
export function useProjectMarkupUpdate({
  projectId
}) {
  const dispatch = useAppDispatch();
  const projectApiClient = useProjectApiClient({
    projectId
  });
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    trackEvent
  } = useTrackEvent();

  /**
   * Updates the status of a markup
   */
  async function updateMarkupStatus({
    markup,
    newStatus,
    statusTemplateId
  }) {
    try {
      // If the new status is "unclassified", for mutation the value should undefined to create delete mutation
      if (newStatus === AnnotationStatus.Unclassified) {
        newStatus = undefined;
      }
      trackEvent({
        name: AnnotationEvents.editAnnotation,
        props: {
          property: "status",
          isValueEmpty: !newStatus
        }
      });
      const mutations = createEditStatusMutation(markup, newStatus, markup.status?.id, statusTemplateId);
      await projectApiClient.applyMutations(mutations);
      return mutations;
    } catch (error) {
      handleErrorWithToast({
        id: `updateAnnotationStatus-${Date.now().toString()}`,
        title: "Could not change annotation status. Please try again",
        error
      });
    }
  }

  /**
   * Handles the removal of a member assigned to an annotation markup.
   *
   * @param assigneeId The ID of the assignee to be removed.
   * @returns Whether the assignee was removed.
   */
  async function removeMember_0(assigneeId) {
    try {
      const removeMember = createMutationDeleteElement(assigneeId);
      trackEvent({
        name: AnnotationEvents.editAnnotation,
        props: {
          property: "assignee",
          isValueEmpty: true
        }
      });

      // Delete the assignee element using projectApiClient
      await projectApiClient.applyMutations([removeMember]);
      return true;
    } catch (error_0) {
      handleErrorWithToast({
        id: `deleteAnnotationAssignee-${Date.now().toString()}`,
        title: "Could not delete the annotation assignee. Please try again",
        error: error_0
      });
      return false;
    }
  }

  /**
   * Adds a new member as an assignee for the markup.
   */
  async function updateNewMember(markup_0, newMember, assigneeTemplateId) {
    try {
      let mutation;
      trackEvent({
        name: AnnotationEvents.editAnnotation,
        props: {
          property: "assignee",
          isValueEmpty: false
        }
      });

      // Edit previous value if it exists
      if (typeof markup_0.assignee !== "undefined") {
        mutation = createMutationUserDirectoryMarkupField(markup_0.assignee.id, [newMember.identity]);
        await projectApiClient.applyMutations([mutation]);
      } else {
        mutation = createMutationAddMarkupField(markup_0.id, {
          id: generateGUID(),
          childrenIds: null,
          name: "Assignee",
          parentId: markup_0.id,
          rootId: markup_0.rootId,
          templateId: assigneeTemplateId,
          type: IElementType.userDirectoryMarkupField,
          typeHint: IElementTypeHint.markupAssigneeId,
          values: [newMember.identity]
        });

        // Create a new node if it did not exists
        await projectApiClient.applyMutations([mutation]);
      }
      return mutation;
    } catch (error_1) {
      handleErrorWithToast({
        id: `updateAnnotationAssignee-${Date.now().toString()}`,
        title: "Could not update the annotation assignee. Please try again",
        error: error_1
      });
    }
  }

  /**  Adds attachments to a markup */
  async function addAttachmentsToMarkup({
    uploadedFiles,
    markup: markup_1,
    attachmentGroup
  }) {
    try {
      const mutations_0 = uploadedFiles.map(uploadedFile => {
        const newAttachment = {
          id: generateGUID(),
          fileName: uploadedFile.fileName,
          downloadUrl: uploadedFile.downloadUrl,
          md5: uploadedFile.md5,
          fileSize: uploadedFile.fileSize,
          date: new Date().toISOString(),
          urlOrFile: uploadedFile.downloadUrl
        };
        return createAttachmentMutation(markup_1.rootId, markup_1.id, newAttachment, attachmentGroup?.id);
      });
      await projectApiClient.applyMutations(mutations_0);
      return mutations_0;
    } catch (error_2) {
      handleErrorWithToast({
        id: `addAttachmentsToMarkup-${Date.now().toString()}`,
        title: "Could not add attachment(s). Please try again",
        error: error_2
      });
    }
  }

  /**  Delete attachment to a markup */
  async function deleteAttachmentToMarkup(attachment) {
    try {
      const deleteAttachment = createMutationDeleteElement(attachment.id);
      await projectApiClient.applyMutations([deleteAttachment]);
      await dispatch(removeIElements([attachment]));
      return deleteAttachment;
    } catch (error_3) {
      handleErrorWithToast({
        id: `deleteAttachmentImage-${Date.now().toString()}`,
        title: "Could not delete the attachment. Please try again",
        error: error_3
      });
    }
  }

  /**  Delete a bulk of attachments from a markup */
  async function bulkDeleteAttachmentsFromMarkup(attachments) {
    const referenceIdToElementId = new Map();
    const deleteAttachments = attachments.map(attachment_0 => {
      const mutation_0 = createMutationDeleteElement(attachment_0.id);
      referenceIdToElementId.set(mutation_0.id, attachment_0.id);
      return mutation_0;
    });
    const finalResults = await applyMutationsWithRetries(deleteAttachments, referenceIdToElementId);
    const successMutations = mapFinalMarkups(finalResults.successMutations, referenceIdToElementId);

    // Attachments are not in the store, but their group is, which holds the attachments count
    void dispatch(removeIElements(attachments.filter(attachment_1 => successMutations.some(({
      elementId
    }) => elementId === attachment_1.id))));
    return {
      successMutations,
      failedMutations: mapFinalMarkups(finalResults.failedMutations, referenceIdToElementId)
    };
  }

  /** Delete a markup from the project */
  async function deleteMarkupFromProject(selectedMarkup) {
    const elementsIdToDelete = shouldDeleteModel3D(selectedMarkup.model3d, selectedMarkup.id) ? selectedMarkup.model3d?.id ?? selectedMarkup.id : selectedMarkup.id;
    const deletedMarkup = createMutationDeleteElement(elementsIdToDelete, true);
    const response = await projectApiClient.applyMutations([deletedMarkup]);
    dispatch(removeIElement(selectedMarkup.id));
    return response[0];
  }

  /** Delete a bulk of markups from the project */
  async function bulkDeleteMarkupsFromProject(selectedMarkups) {
    const referenceIdToElementId_0 = new Map();
    const deletedMarkups = createDeletedMarkups(selectedMarkups, referenceIdToElementId_0);
    const finalResults_0 = await applyMutationsWithRetries(deletedMarkups, referenceIdToElementId_0);
    return {
      successMutations: mapFinalMarkups(finalResults_0.successMutations, referenceIdToElementId_0),
      failedMutations: mapFinalMarkups(finalResults_0.failedMutations, referenceIdToElementId_0)
    };
  }

  // Maps MutationResults to MarkupDeletionResults by adding the corresponding 'elementId' to each one .
  function mapFinalMarkups(results, refIdToElementId) {
    return results.map(res => ({
      ...res,
      elementId: refIdToElementId.get(res.referenceId ?? "") ?? ""
    }));
  }

  /** Create delete mutations for markups */
  function createDeletedMarkups(selectedMarkups_0, referenceIdToElementId_1) {
    return selectedMarkups_0.map(markup_2 => {
      const elementId_0 = shouldDeleteModel3D(markup_2.model3d, markup_2.id) ? markup_2.model3d?.id ?? markup_2.id : markup_2.id;
      const mutation_1 = createMutationDeleteElement(elementId_0, true);
      // Map reference id to element id, so we don't have to search for the markup id later
      referenceIdToElementId_1.set(mutation_1.id, markup_2.id);
      return mutation_1;
    });
  }

  /** Apply mutations with retries */
  async function applyMutationsWithRetries(toProcess, referenceIdToElementId_2, accumulatedSuccess = [], accumulatedFailure = [], accumulatedPending = []) {
    function removeDeletedElements(results_0) {
      for (const {
        referenceId
      } of results_0) {
        const elementId_1 = referenceIdToElementId_2.get(referenceId ?? "");
        if (elementId_1) {
          dispatch(removeIElement(elementId_1));
        }
      }
    }
    while (toProcess.length > 0) {
      try {
        const response_1 = await projectApiClient.applyMutations(toProcess, true);
        accumulatedSuccess.push(...response_1);
        removeDeletedElements(response_1);
        return {
          successMutations: accumulatedSuccess,
          failedMutations: accumulatedFailure,
          pendingMutations: accumulatedPending
        };
      } catch (error_4) {
        if (error_4 instanceof ApiResponseError) {
          const response_0 = error_4.body.data;
          const newSuccess = response_0.filter(r => r.status === "success");
          const newFailure = response_0.filter(r_0 => r_0.status === "failure");
          const newPending = response_0.filter(r_1 => r_1.status === "pending");
          accumulatedSuccess.push(...newSuccess);
          accumulatedFailure.push(...newFailure);
          accumulatedPending.push(...newPending);
          removeDeletedElements(newSuccess);
          if (newPending.length > 0) {
            const pendingIds = new Set(newPending.map(p => p.referenceId));
            toProcess = toProcess.filter(m => pendingIds.has(m.id));
          } else {
            return {
              successMutations: accumulatedSuccess,
              failedMutations: accumulatedFailure,
              pendingMutations: accumulatedPending
            };
          }
        } else {
          throw error_4;
        }
      }
    }
    return {
      successMutations: accumulatedSuccess,
      failedMutations: accumulatedFailure,
      pendingMutations: accumulatedPending
    };
  }
  return {
    updateMarkupStatus,
    updateNewMember,
    removeMember: removeMember_0,
    addAttachmentsToMarkup,
    deleteAttachmentToMarkup,
    deleteMarkupFromProject,
    bulkDeleteMarkupsFromProject,
    bulkDeleteAttachmentsFromMarkup
  };
}